import React from 'react'
import { motion } from 'framer-motion'
import { Search, Map, FileText, HeartHandshake } from 'lucide-react'
import Container from './Container'

const AboutBavarianSima: React.FC = () => {
  return (
    <section className="bg-primary text-white py-32 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/20 blur-[120px] rounded-[100%] pointer-events-none mix-blend-screen opacity-50"></div>

      <Container size="xl" className="relative z-10">
        
        {/* Intro Section - Centered */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-4xl mx-auto mb-24"
        >
          <div className="flex justify-center mb-8">
            <img 
              src="/assets/bavarian-logo-white.png" 
              alt="Bavarian Real Estate Logo" 
              className="h-16 w-auto opacity-90 object-contain drop-shadow-2xl"
            />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-8 tracking-tight text-white">
            El Viaje del Inversor
          </h2>
          <p className="text-xl text-secondary-light/80 font-light leading-relaxed max-w-3xl mx-auto">
            En <span className="text-white font-medium">Bavarian Real Estate</span> guiamos tus decisiones con un proceso sumamente estructurado. Entendemos tu objetivo, filtramos con rigor y te acompañamos paso a paso con absoluta claridad.
          </p>
        </motion.div>

        {/* Timeline Horizontal Layout */}
        <div className="relative mt-8">
          {/* Desktop Connecting Line */}
          <div className="hidden lg:block absolute top-[80px] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-accent/0 via-accent/30 to-accent/0 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative z-10">
            {[
              {
                icon: Search,
                title: 'Diagnóstico Rápido',
                desc: 'Analizamos tu objetivo exacto, timing y presupuesto para entender tu perfil sin pérdidas de tiempo.'
              },
              {
                icon: Map,
                title: 'Selección Inteligente',
                desc: 'Filtramos todo el mercado y te presentamos únicamente las propiedades que encajan a la perfección.'
              },
              {
                icon: FileText,
                title: 'Claridad Total',
                desc: 'Te explicamos cada documento legal, plan de pagos y los próximos pasos de tu inversión.'
              },
              {
                icon: HeartHandshake,
                title: 'Acompañamiento VIP',
                desc: 'A tu lado desde la primera llamada en Madrid hasta la entrega final de llaves en República Dominicana.'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.8, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
                className="group relative flex flex-col h-full items-center text-center rounded-[2.5rem] border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-8 md:p-10 backdrop-blur-xl transition-all duration-500 hover:bg-white/[0.06] hover:border-white/20 hover:shadow-[0_20px_50px_-15px_rgba(2,132,199,0.2)] hover:-translate-y-2 overflow-hidden"
              >
                {/* Background Number */}
                <div
                  aria-hidden="true"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 text-[10rem] font-bold leading-none text-white/[0.02] transition-all duration-700 group-hover:text-accent/[0.04] group-hover:scale-110 font-display pointer-events-none"
                >
                  0{index + 1}
                </div>
                
                {/* Icon inside a glowing orb */}
                <div className="relative mb-8 flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-3xl bg-accent/10 text-accent-light shadow-[0_0_20px_-5px_rgba(2,132,199,0.3)] transition-all duration-500 group-hover:bg-accent group-hover:text-white group-hover:shadow-[0_0_30px_-5px_rgba(2,132,199,0.6)]">
                  {/* Subtle ring around the icon */}
                  <div className="absolute inset-0 rounded-3xl border border-accent/20 group-hover:border-white/30 transition-colors duration-500"></div>
                  <step.icon className="h-8 w-8 relative z-10" />
                </div>
                
                <h3 className="mb-4 font-display text-2xl font-bold text-white tracking-tight transition-colors duration-300 group-hover:text-accent-light leading-tight px-2">
                  {step.title}
                </h3>
                
                <p className="text-secondary-light/70 text-base font-light leading-relaxed">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

      </Container>
    </section>
  )
}

export default AboutBavarianSima
